// Layer-opacity drag preview surface (layer-display-opacity-2026-10-12.md).
//
// `EngineCore.beginOpacityPreview` hands the backend three full-canvas RGBA
// sheets (visible layers below the target / the target alone / visible layers
// above). This module turns them into scratch canvases ONCE; each frame is
// then only
//
//   clearRect → drawImage(below) → globalAlpha=v drawImage(target)
//             → globalAlpha=1 drawImage(above)
//
// — no JS pixel work, so a 2000² × 5 layer drag stays smooth. Shared by the
// worker (OffscreenCanvas) and the inline path (HTMLCanvasElement) via the
// `createCanvas` factory. The display canvas is a transparent surface (the
// flat bg colour comes from CSS), so the 3-sheet source-over chain equals the
// normal composite (up to rounding; the end-of-drag `repaint` is exact).

import type { OpacityPreviewSheets } from '@oekaki/drawing-core';

/** The slice of a 2D context the preview needs (structural: DOM + Offscreen both fit). */
export interface PreviewCtx {
  globalAlpha: number;
  clearRect(x: number, y: number, w: number, h: number): void;
  drawImage(image: unknown, dx: number, dy: number): void;
  putImageData(data: ImageData, dx: number, dy: number): void;
}

export interface PreviewCanvas {
  getContext(type: '2d'): PreviewCtx | null;
}

export interface OpacityPreviewSurface {
  begin(sheets: OpacityPreviewSheets): void;
  draw(opacity: number): void;
  end(): void;
}

export const createOpacityPreviewSurface = (
  display: PreviewCtx,
  createCanvas: (width: number, height: number) => PreviewCanvas & object,
): OpacityPreviewSurface => {
  let sheets: { below: object | null; target: object | null; above: object | null } | null = null;
  let size = { width: 0, height: 0 };

  const toCanvas = (rgba: Uint8ClampedArray | null, w: number, h: number): object | null => {
    if (rgba === null) return null;
    const c = createCanvas(w, h);
    const ctx = c.getContext('2d');
    if (ctx === null) return null;
    ctx.putImageData(new ImageData(rgba as Uint8ClampedArray<ArrayBuffer>, w, h), 0, 0);
    return c;
  };

  const draw = (opacity: number): void => {
    if (sheets === null) return;
    display.clearRect(0, 0, size.width, size.height);
    if (sheets.below !== null) display.drawImage(sheets.below, 0, 0);
    if (sheets.target !== null) {
      display.globalAlpha = opacity;
      display.drawImage(sheets.target, 0, 0);
      display.globalAlpha = 1;
    }
    if (sheets.above !== null) display.drawImage(sheets.above, 0, 0);
  };

  return {
    begin(s) {
      size = { width: s.width, height: s.height };
      sheets = {
        below: toCanvas(s.below, s.width, s.height),
        target: toCanvas(s.target, s.width, s.height),
        above: toCanvas(s.above, s.width, s.height),
      };
    },
    draw,
    end() {
      sheets = null;
      display.globalAlpha = 1;
    },
  };
};
